import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  PhoneOff,
  Users,
  Video,
  VideoOff,
} from 'lucide-react';
import { LessonParticipantRole, UserRole } from '@tutor/shared';
import { Alert, Avatar, Badge, Button, ErrorState, LoadingState, cn } from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth.store';
import { useClassroomAccess } from '../../hooks/queries';
import { useClassroom } from '../../hooks/useClassroom';
import { useWebRTC } from '../../hooks/useWebRTC';
import { Whiteboard } from '../../components/classroom/Whiteboard';
import { ClassroomChat } from '../../components/classroom/ClassroomChat';
import { VideoPanel, type VideoParticipant } from '../../components/classroom/VideoPanel';
import { formatTime } from '../../lib/format';

type SidePanel = 'chat' | 'people';

export function ClassroomPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [panel, setPanel] = useState<SidePanel>('chat');

  const { data: access, isLoading, error } = useClassroomAccess(lessonId);

  const classroom = useClassroom(lessonId, Boolean(access));

  const iceServers = useMemo(() => access?.iceServers ?? [], [access?.iceServers]);

  const { sendMediaState } = classroom;
  const onMediaStateChange = useCallback(
    (state: { audioEnabled: boolean; videoEnabled: boolean; screenSharing: boolean }) => {
      sendMediaState(state);
    },
    [sendMediaState],
  );

  // Peer connections are only built once the room is actually joined — before that
  // the server has no presence record to negotiate against.
  const media = useWebRTC(
    lessonId,
    classroom.connection === 'connected',
    iceServers,
    onMediaStateChange,
  );

  const activePage = useMemo(
    () => classroom.pages.find((page) => page.id === classroom.activePageId) ?? classroom.pages[0] ?? null,
    [classroom.pages, classroom.activePageId],
  );

  // Presence supplies the names and mic state; WebRTC supplies the streams.
  const videoParticipants = useMemo<VideoParticipant[]>(() => {
    if (!user) return [];

    const self: VideoParticipant = {
      userId: user.id,
      fullName: user.fullName,
      avatarUrl: user.avatarUrl,
      isSelf: true,
      stream: media.localStream,
      hasVideo: media.videoEnabled,
      audioEnabled: media.audioEnabled,
      screenSharing: media.screenSharing,
    };

    const remote = media.remotePeers.map((peer) => {
      const presence = classroom.participants.find((p) => p.userId === peer.userId);
      return {
        userId: peer.userId,
        fullName: presence?.fullName ?? 'Participant',
        avatarUrl: presence?.avatarUrl ?? null,
        isSelf: false,
        stream: peer.stream,
        // Trust the peer's own broadcast over track state: `replaceTrack(null)` leaves
        // the receiver holding a live-but-frozen track rather than an ended one.
        hasVideo: presence?.media.videoEnabled ?? peer.hasVideo,
        audioEnabled: presence?.media.audioEnabled ?? peer.hasAudio,
        screenSharing: presence?.media.screenSharing ?? false,
      } satisfies VideoParticipant;
    });

    return [self, ...remote];
  }, [user, media.localStream, media.videoEnabled, media.audioEnabled, media.screenSharing, media.remotePeers, classroom.participants]);

  const isTutor = user?.role === UserRole.TUTOR;
  const basePath = isTutor ? '/tutor' : '/student';

  // When the tutor ends the lesson, everyone is returned to their dashboard.
  useEffect(() => {
    if (classroom.connection !== 'ended') return;
    const timer = window.setTimeout(() => navigate(`${basePath}/dashboard`, { replace: true }), 3_000);
    return () => window.clearTimeout(timer);
  }, [classroom.connection, navigate, basePath]);

  if (isLoading) return <LoadingState label="Opening the classroom…" />;

  // The server's answer is the authority: a 403 here means this user is genuinely not
  // in this lesson, whatever route they used to arrive.
  if (error) {
    const apiError = error instanceof ApiError ? error : null;
    const isDenied = apiError?.isForbidden ?? false;

    return (
      <div className="mx-auto max-w-lg py-16">
        <ErrorState
          title={isDenied ? 'You do not have access to this classroom' : 'We could not open this classroom'}
          description={
            apiError?.message ??
            'Something went wrong on the way in. Please go back and try again from your dashboard.'
          }
          action={
            <Link to={`${basePath}/dashboard`}>
              <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden />}>
                Back to dashboard
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  if (!access || !user) return null;

  const { lesson, capabilities } = access;
  const isHost = access.participantRole === LessonParticipantRole.HOST;

  const handleEndLesson = async (): Promise<void> => {
    await classroom.endLesson();
    navigate(`${basePath}/dashboard`, { replace: true });
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-900">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-700 bg-slate-800 px-4 text-white">
        <Link
          to={`${basePath}/lessons/${lesson.id}`}
          className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Leave</span>
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-sm font-semibold">{lesson.title}</h1>
            {lesson.subject && <Badge tone="brand">{lesson.subject.name}</Badge>}
            {classroom.connection === 'connected' ? (
              <Badge tone="success" dot>
                Live
              </Badge>
            ) : classroom.connection === 'ended' ? (
              <Badge tone="neutral">Ended</Badge>
            ) : (
              <Badge tone="warning">Connecting…</Badge>
            )}
          </div>
          <p className="truncate text-xs text-slate-400">
            {formatTime(lesson.scheduledStart)}–{formatTime(lesson.scheduledEnd)}
            {lesson.topic && ` · ${lesson.topic.name}`}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={media.audioEnabled}
            aria-label={media.audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            disabled={media.isRequestingMedia}
            onClick={() => void media.toggleAudio()}
            className={cn(
              'text-slate-300 hover:bg-slate-700 hover:text-white',
              media.audioEnabled ? 'bg-slate-700 text-white' : 'bg-rose-600/20 text-rose-300',
            )}
          >
            {media.audioEnabled ? <Mic className="h-4 w-4" aria-hidden /> : <MicOff className="h-4 w-4" aria-hidden />}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            aria-pressed={media.videoEnabled}
            aria-label={media.videoEnabled ? 'Turn camera off' : 'Turn camera on'}
            disabled={media.isRequestingMedia}
            onClick={() => void media.toggleVideo()}
            className={cn(
              'text-slate-300 hover:bg-slate-700 hover:text-white',
              media.videoEnabled ? 'bg-slate-700 text-white' : 'bg-rose-600/20 text-rose-300',
            )}
          >
            {media.videoEnabled ? <Video className="h-4 w-4" aria-hidden /> : <VideoOff className="h-4 w-4" aria-hidden />}
          </Button>

          {capabilities.canShareScreen && (
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={media.screenSharing}
              aria-label={media.screenSharing ? 'Stop sharing screen' : 'Share screen'}
              disabled={media.isRequestingMedia}
              onClick={() => void media.toggleScreenShare()}
              className={cn(
                'hidden text-slate-300 hover:bg-slate-700 hover:text-white sm:inline-flex',
                media.screenSharing && 'bg-emerald-600/30 text-emerald-300',
              )}
            >
              <MonitorUp className="h-4 w-4" aria-hidden />
            </Button>
          )}

          {capabilities.canEndLesson && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleEndLesson}
              leftIcon={<PhoneOff className="h-4 w-4" aria-hidden />}
            >
              <span className="hidden sm:inline">End lesson</span>
            </Button>
          )}
        </div>
      </header>

      {classroom.connection === 'ended' && (
        <Alert tone="info" className="m-3">
          This lesson has ended. Taking you back to your dashboard…
        </Alert>
      )}

      {classroom.error && classroom.connection === 'error' && (
        <Alert tone="danger" className="m-3">
          {classroom.error}
        </Alert>
      )}

      {media.mediaError && (
        <Alert tone="warning" className="m-3">
          {media.mediaError}
        </Alert>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1 bg-white">
          <VideoPanel participants={videoParticipants} />

          {classroom.connection === 'connecting' || classroom.connection === 'joining' ? (
            <LoadingState label="Connecting to the classroom…" className="h-full" />
          ) : (
            <Whiteboard
              page={activePage}
              pages={classroom.pages}
              activePageId={classroom.activePageId}
              cursors={classroom.cursors}
              previews={classroom.previews}
              canDraw={capabilities.canDraw}
              canManagePages={capabilities.canManageWhiteboardPages}
              canClear={capabilities.canClearWhiteboard}
              onStroke={classroom.sendStroke}
              onPreview={classroom.sendStrokePreview}
              onUndo={classroom.undo}
              onClear={classroom.clearPage}
              onAddPage={classroom.addPage}
              onSelectPage={classroom.setActivePage}
            />
          )}
        </div>

        <aside className="hidden w-80 shrink-0 flex-col border-l border-slate-200 bg-white lg:flex">
          <div className="flex shrink-0 border-b border-slate-200">
            <button
              type="button"
              onClick={() => setPanel('chat')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium',
                panel === 'chat' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <MessageSquare className="h-4 w-4" aria-hidden />
              Chat
            </button>
            <button
              type="button"
              onClick={() => setPanel('people')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium',
                panel === 'people' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <Users className="h-4 w-4" aria-hidden />
              People ({classroom.participants.length})
            </button>
          </div>

          {panel === 'chat' ? (
            <ClassroomChat
              messages={classroom.messages}
              typingUsers={classroom.typingUsers}
              currentUserId={user.id}
              canChat={capabilities.canChat}
              onSend={classroom.sendMessage}
              onTyping={classroom.setTyping}
            />
          ) : (
            <div className="flex-1 overflow-y-auto p-3">
              <ul className="space-y-2">
                {classroom.participants.map((participant) => {
                  const isSelf = participant.userId === user.id;
                  // The server broadcasts media state to everyone *except* the sender,
                  // so our own row has to read from local state instead of presence.
                  const mediaState = isSelf
                    ? {
                        audioEnabled: media.audioEnabled,
                        videoEnabled: media.videoEnabled,
                        screenSharing: media.screenSharing,
                      }
                    : participant.media;

                  return (
                    <li key={participant.userId} className="flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50">
                      <Avatar name={participant.fullName} src={participant.avatarUrl} size="sm" status="online" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {participant.fullName}
                          {isSelf && <span className="ml-1 text-xs font-normal text-slate-400">(you)</span>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {participant.participantRole === LessonParticipantRole.HOST ? 'Tutor' : 'Student'}
                          {mediaState.screenSharing && ' · sharing screen'}
                        </p>
                      </div>
                      {mediaState.videoEnabled && (
                        <Video className="h-3.5 w-3.5 text-emerald-500" aria-label="Camera on" />
                      )}
                      {!mediaState.audioEnabled && (
                        <MicOff className="h-3.5 w-3.5 text-slate-300" aria-label="Microphone off" />
                      )}
                    </li>
                  );
                })}
                {classroom.participants.length === 0 && (
                  <li className="py-8 text-center text-sm text-slate-400">
                    Waiting for {isHost ? 'your student' : 'your tutor'} to join…
                  </li>
                )}
              </ul>

              {!capabilities.canDraw && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  You are observing this lesson and cannot draw on the board.
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
